/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-03-24 22:41:02
 */
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../app/store'
import { Modal } from 'antd'
import { capturePromiseException } from '../utils/sentryUtils'
import { buildParamsWithAuthPolicy } from '../actions/photo'
import { Photo } from '../global'
import fetchHelper from '../utils/fetchHelper'
import { enableMapSet } from 'immer'

enableMapSet()

// 声明 Vite define 定义的全局变量
declare const __API_SERVER_HOST__: string

type LocationRecommend = {
  word: string
  key: string
}

// 定义初始状态类型
interface PhotosByLocationState {
  photos: Photo[]
  checkedPhotos: Set<number>
  unCheckedPhotos: Set<number>
  checkAll: boolean
  loading: boolean
  lastPageCond: string
  photosNum: number
  locationRecommend: LocationRecommend | null
}

// 初始状态
const initialState: PhotosByLocationState = {
  photos: [],
  checkedPhotos: new Set<number>(),
  unCheckedPhotos: new Set<number>(),
  checkAll: false,
  loading: false,
  lastPageCond: '',
  photosNum: 0,
  locationRecommend: null
}

// 创建异步 thunk
export const fetchPhotosByLocation = createAsyncThunk(
  'photosByLocation/fetchPhotos',
  async (data: any, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, data))
        }
      }
      const result = await fetchHelper(dispatch, getState, config)
      const { payload, status } = result
      if (status === 0) {
        // 确保返回的数据结构与 reducer 中期望的一致
        return {
          photos: payload.photos || [],
          expandInfo: payload.expandInfo || {},
          lastPageCond: payload.lastPageCond || '',
          photosNum: payload.total || 0
        }
      } else if (status === -3) {
        Modal.warning({
          title: '提示',
          content: '您已退出或被移出当前群',
          onOk: () => {
            window.location.href = '/'
          }
        })
        return rejectWithValue('您已退出或被移出当前群')
      } else if (status === -34) {
        Modal.warning({
          title: '提示',
          content:
            '照片搜索服务正在升级中，预计2月20日升级完成。升级期间的照片数据会安全保存，不会丢失。'
        })
        return rejectWithValue('照片搜索服务正在升级中')
      } else if (status === -991) {
        Modal.warning({
          title: '提示',
          content: '暂无照片查看权限'
        })
        return {
          photos: [],
          expandInfo: {},
          lastPageCond: '',
          photosNum: 0
        }
      }

      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

// 创建加载更多照片的异步 thunk
export const fetchMorePhotosByLocation = createAsyncThunk(
  'photosByLocation/fetchMorePhotos',
  async (data: any, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, data))
        }
      }
      const result = await fetchHelper(dispatch, getState, config)
      const { payload, status } = result

      if (status === 0) {
        return {
          photos: payload.photos || [],
          lastPageCond: payload.lastPageCond || ''
        }
      } else if (status === -3) {
        Modal.warning({
          title: '提示',
          content: '您已退出或被移出当前群',
          onOk: () => {
            window.location.href = '/'
          }
        })
        return rejectWithValue('您已退出或被移出当前群')
      } else if (status === -991) {
        Modal.warning({
          title: '提示',
          content: '暂无照片查看权限'
        })
        return rejectWithValue('暂无照片查看权限')
      }

      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

// 创建 slice
const photosByLocationSlice = createSlice({
  name: 'photosByLocation',
  initialState,
  reducers: {
    setLocationRecommend: (
      state,
      action: PayloadAction<LocationRecommend | null>
    ) => {
      state.locationRecommend = action.payload
    },
    pushCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.checkedPhotos.add(action.payload)
    },
    removeCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.checkedPhotos.delete(action.payload)
    },
    pushUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.unCheckedPhotos.add(action.payload)
    },
    removeUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.unCheckedPhotos.delete(action.payload)
    },
    setCheckAll: (state) => {
      state.checkAll = true
      state.checkedPhotos.clear()
      state.unCheckedPhotos.clear()
    },
    clearCheck: (state) => {
      state.unCheckedPhotos.clear()
      state.checkedPhotos.clear()
      state.checkAll = false
    },
    setPhotos: (state, action: PayloadAction<Photo[]>) => {
      state.photos = action.payload
    },
    reset: (state) => {
      state.photos = []
      state.checkedPhotos.clear()
      state.unCheckedPhotos.clear()
      state.checkAll = false
      state.loading = false
      state.lastPageCond = ''
      state.photosNum = 0
      state.locationRecommend = null
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理 fetchPhotosByLocation
      .addCase(fetchPhotosByLocation.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPhotosByLocation.fulfilled, (state, action) => {
        if (action.payload) {
          state.photos = action.payload.photos || []
          state.lastPageCond = action.payload.lastPageCond || ''
          state.photosNum = action.payload.photosNum || 0
          state.loading = false
        }
      })
      .addCase(fetchPhotosByLocation.rejected, (state, action) => {
        state.loading = false
      })
      // 处理 fetchMorePhotosByLocation
      .addCase(fetchMorePhotosByLocation.fulfilled, (state, action) => {
        state.photos.push(...action.payload.photos)
        state.lastPageCond = action.payload.lastPageCond
      })
      .addCase(fetchMorePhotosByLocation.rejected, (state) => {
        // 处理错误
      })
  }
})

// 导出 actions
export const {
  setLocationRecommend,
  pushCheckedPhoto,
  removeCheckedPhoto,
  pushUncheckedPhoto,
  removeUncheckedPhoto,
  setCheckAll,
  clearCheck,
  setPhotos,
  reset
} = photosByLocationSlice.actions

// 导出选择器
export const selectCheckedPhotos = (state: RootState) =>
  Array.from(state.photosByLocation.checkedPhotos)

export const selectUncheckedPhotos = (state: RootState) =>
  Array.from(state.photosByLocation.unCheckedPhotos)

export const selectCheckAll = (state: RootState) =>
  state.photosByLocation.checkAll

export const selectPhotos = (state: RootState) => state.photosByLocation.photos

export const selectLoading = (state: RootState) =>
  state.photosByLocation.loading

export const selectLastPageCond = (state: RootState) =>
  state.photosByLocation.lastPageCond

export const selectPhotosNum = (state: RootState) =>
  state.photosByLocation.photosNum

export const selectLocationRecommend = (state: RootState) =>
  state.photosByLocation.locationRecommend

// 导出 reducer
export default photosByLocationSlice.reducer
